import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Toast } from '../components/ui/Toast';
import { NoodstopSheet } from '../components/ui/NoodstopSheet';
import { WallAtmosphere } from '../components/wall/WallAtmosphere';
import { WallTopBar } from '../components/wall/WallTopBar';
import { WallBottomBar } from '../components/wall/WallBottomBar';
import { ListenOverlay } from '../components/wall/ListenOverlay';
import { MomentControl } from '../components/wall/MomentControl';
import { OrbitScene } from '../components/wall/orbit/OrbitScene';
import { StackScene } from '../components/wall/StackScene';
import { useAnnaStage } from '../components/wall/useAnnaStage';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useViewport } from '../hooks/useViewport';
import { useIdleCalm } from '../hooks/useIdleCalm';
import { wallGeometry } from '../utils/orbitLayout';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Anna-modus on the wall tablet (1920×1200, verified down to 1280×800). Anna fills the room; everything else orbits her. */
export function AnnaWall() {
  const stage = useAnnaStage();
  const {
    w,
    h
  } = useViewport();
  const [typing, setTyping] = useState(false);
  const [micNote, setMicNote] = useState<string | null>(null);
  const rec = useSpeechRecognition(text => stage.respond(text));
  const {
    setListening
  } = stage;
  useEffect(() => {
    setListening(rec.listening || typing);
  }, [rec.listening, typing, setListening]);
  useEffect(() => {
    if (!rec.error) return;
    setMicNote(rec.error === 'no-speech' ? 'Ik hoorde niets. Probeer het nog eens, of typ.' : 'Spraak werkt hier niet (microfoon geblokkeerd). Typ je vraag.');
    setTyping(true);
  }, [rec.error]);
  useEffect(() => {
    if (!stage.toast) return;
    const id = window.setTimeout(stage.dismissToast, 4000);
    return () => window.clearTimeout(id);
  }, [stage.toast, stage.dismissToast]);
  const onMic = () => {
    if (rec.listening) {
      rec.stop();
      return;
    }
    stage.bargeIn();
    setTyping(false);
    setMicNote(null);
    stage.sound('klaar');
    if (!rec.start()) {
      setMicNote('Spraakherkenning werkt niet in deze browser. Typ je vraag.');
      setTyping(true);
    }
  };
  const onType = () => {
    if (rec.listening) rec.abort();
    stage.bargeIn();
    setMicNote(null);
    setTyping(true);
  };
  const submitText = (text: string) => {
    setTyping(false);
    setMicNote(null);
    stage.respond(text);
  };
  const listening = rec.listening || typing;
  const nothingGoingOn = stage.view === 'stage' && !stage.caption && !stage.quietLine && stage.cards.length === 0 && stage.activity === 'idle' && !listening && !stage.stopped;
  const calm = useIdleCalm(nothingGoingOn);
  const g = wallGeometry(w, h, stage.view);
  return <div className="relative h-screen min-h-[560px] w-full overflow-hidden bg-night-950 text-ink" data-id="element-1792" data-mp="3l97mv523esl2">
      <WallAtmosphere daypart={stage.view === 'sleep' ? 'nacht' : stage.daypart} cy={`${(g.cy / h * 100).toFixed(1)}%`} data-id="element-1793" data-mp="3tpf2z6p6rcp4" />

      {/* Wake: everything but Anna dims while she listens */}
      <AnimatePresence data-id="element-1794" data-mp="6cw1chlu9xej">
        {listening && <motion.div aria-hidden className="pointer-events-none absolute inset-0 z-[12] bg-night-950/55" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} data-id="element-1795" data-mp="6fh3l7q1ufct" />}
      </AnimatePresence>

      {g.mode === 'orbit' ? <OrbitScene stage={stage} g={g} nothingGoingOn={nothingGoingOn} listening={listening} data-id="element-1796" data-mp="3jr92mtj12emd" /> : <StackScene stage={stage} g={g} nothingGoingOn={nothingGoingOn} data-id="element-1797" data-mp="3os6iv0kh8yl8" />}

      <MomentControl index={stage.momentIndex} count={stage.momentCount} title={stage.moment.title} onPrev={stage.prev} onNext={stage.next} data-id="element-1798" data-mp="z2uscijlg4lu" />
      <WallTopBar stage={stage} calm={calm} data-id="element-1799" data-mp="13zbhng2j4p0i" />

      <ListenOverlay listening={rec.listening} interim={rec.interim} typing={typing} note={micNote} activity={stage.activity} thinkingWhere={stage.thinkingWhere} onSubmit={submitText} onCloseTyping={() => {
      setTyping(false);
      setMicNote(null);
    }} data-id="element-1800" data-mp="2cxl20ucr6sfp" />

      <WallBottomBar stage={stage} listening={listening} calm={calm} showThreads={g.mode === 'stack'} onMic={onMic} onType={onType} data-id="element-1801" data-mp="2gyd9pj5z8p9v" />

      <div className="pointer-events-none fixed inset-x-0 bottom-36 z-50 flex justify-center px-4" data-id="element-1802" data-mp="2qxvvm55g6l4k">
        <AnimatePresence data-id="element-1803" data-mp="34p0wtbogrlh0">
          {stage.toast && <motion.div key={stage.toast.id} className="pointer-events-auto flex w-full justify-center" initial={{
          opacity: 0,
          y: 12,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} data-id="element-1804" data-mp="34or25ash4q4z">
              <Toast size="wall" tone={stage.toast.tone} title={stage.toast.text} onClose={stage.dismissToast} data-id="element-1805" data-mp="3t9m0tmh5dvcj" />
            </motion.div>}
        </AnimatePresence>
      </div>

      <NoodstopSheet size="wall" open={stage.stopped} onResume={stage.resume} data-id="element-1806" data-mp="3jc7xmok8j326" />
    </div>;
}